import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faBars,
  faXmark,
  faRightFromBracket,
  faChalkboardTeacher,
  faUsers,
  faUserGraduate,
  faBookOpen,
  faAward,
  faTrophy,
  faMedal,
  faMoneyBillWave,
  faTag,
  faGift,
  faCalendarDays,
  faEnvelope,
  faSun,
  faMoon,
  faUserShield,
  faGear,
  faChartLine,
  faCommentDots,
} from '@fortawesome/free-solid-svg-icons';
import NotificationBell from './NotificationBell';
import './Layout.css';

Modal.setAppElement('#root');

const API = process.env.REACT_APP_API_BASE_URL;
const LOGO_DARK = '/assets/img/logo/logowhitenew.png';
const LOGO_LIGHT = '/assets/img/logo/logonew.png';
const THEME_KEY = 'rm-demo-theme'; // same key as the other dashboards
const SIDE_KEY = 'rm-admin-side'; // remembers if the menu is hidden on a computer
const isPhone = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches;

// Same pages as before, grouped. "also" = other routes that light up the same item.
const NAV = [
  {
    group: 'Overview',
    items: [
      { to: '/Admin/Dashboard', icon: faHome, label: 'Dashboard' },
      { to: '/Admin/Visitors', icon: faChartLine, label: 'Visitors' },
    ],
  },
  {
    group: 'Teaching',
    items: [
      { to: '/Admin/MyClasses', icon: faChalkboardTeacher, label: 'Classes' },
      { to: '/Admin/MyadminStudents', icon: faUserGraduate, label: 'Students' },
      { to: '/Admin/Curriculum', icon: faBookOpen, label: 'Curriculum' },
      { to: '/Admin/MyCertificates', icon: faAward, label: 'Certificates' },
      { to: '/Admin/Competitions', icon: faTrophy, label: 'Competitions' },
      { to: '/Admin/Badges', icon: faMedal, label: 'Badges' },
    ],
  },
  {
    group: 'People',
    items: [
      { to: '/Admin/users', icon: faUsers, label: 'Users', also: ['/admin/teacherprofile', '/admin/studentprofile'] },
      { to: '/Admin/ParentFeedback', icon: faCommentDots, label: 'Parent feedback' },
    ],
  },
  {
    group: 'Business',
    items: [
      { to: '/Admin/MyEarnings', icon: faMoneyBillWave, label: 'Earnings' },
      { to: '/Admin/PricesAndOffers', icon: faTag, label: 'Prices & Offers' },
      { to: '/Admin/Referrals', icon: faGift, label: 'Referrals' },
      { to: '/Admin/MyEvents', icon: faCalendarDays, label: 'Events' },
      { to: '/Admin/sendemails', icon: faEnvelope, label: 'Send Emails' },
    ],
  },
  {
    group: 'System',
    items: [{ to: '/Admin/Settings', icon: faGear, label: 'Settings' }],
  },
];

const safe = {
  get: (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* private mode */
    }
  },
};

const readToken = () => {
  try {
    const raw = localStorage.getItem('userData');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [token] = useState(readToken);

  const [menuOpen, setMenuOpen] = useState(false); // phones: drawer open
  const [sideHidden, setSideHidden] = useState(() => safe.get(SIDE_KEY) === 'hidden'); // computers: menu hidden
  const [showLogout, setShowLogout] = useState(false);
  const [theme, setTheme] = useState(() => (safe.get(THEME_KEY) === 'dark' ? 'dark' : 'light'));
  const [userData, setUserData] = useState(null);

  const isLight = theme === 'light';
  const path = location.pathname.toLowerCase();

  // ---------- Admin profile (same endpoint, same fields) ----------
  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API}/api/admin/layout`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (!response.ok) throw new Error('Unauthorized');
        const data = await response.json();
        if (!cancelled) {
          setUserData({
            id: data.id,
            username: data.username,
            firstName: data.firstName,
            lastName: data.lastName,
            Image: data.Image,
            fullName: [data.firstName, data.lastName].filter(Boolean).join(' '),
          });
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
        if (!cancelled) navigate('/login', { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  // Close the mobile menu when the page changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // One button: opens/closes the drawer on phones, shows/hides the menu on computers
  const toggleMenu = () => {
    if (isPhone()) {
      setMenuOpen((v) => !v);
      return;
    }
    setSideHidden((v) => {
      safe.set(SIDE_KEY, v ? 'shown' : 'hidden');
      return !v;
    });
  };

  const toggleTheme = () => {
    const next = isLight ? 'dark' : 'light';
    setTheme(next);
    safe.set(THEME_KEY, next);
  };

  const handleLogout = () => {
    localStorage.removeItem('userData');
    navigate('/login', { replace: true });
  };

  const fullName = userData?.fullName || '';

  // Pass userData to the page, like before
  const childrenWithProps = React.Children.map(children, (child) =>
    React.isValidElement(child) ? React.cloneElement(child, { userData }) : child
  );

  return (
    <div className={`rmadl ${sideHidden ? 'is-side-hidden' : ''}`} data-theme={theme}>
      {/* ---------- Top bar ---------- */}
      <header className="rmadl-top">
        <div className="rmadl-top-left">
          <button
            type="button"
            className="rmadl-menu-btn"
            onClick={toggleMenu}
            aria-label={menuOpen ? 'Close menu' : sideHidden ? 'Show menu' : 'Hide menu'}
            title={menuOpen ? 'Close menu' : sideHidden ? 'Show menu' : 'Hide menu'}
            aria-expanded={menuOpen || !sideHidden}
            aria-controls="rmadl-side"
          >
            <FontAwesomeIcon icon={menuOpen ? faXmark : faBars} />
          </button>
          <Link to="/Admin/Dashboard" className="rmadl-logo" aria-label="Dashboard">
            <img src={isLight ? LOGO_LIGHT : LOGO_DARK} alt="RoboMind" />
          </Link>
        </div>

        <div className="rmadl-top-right">
          <NotificationBell base={`${API}/api/admin/notifications`} token={token} withActivity allLink="/Admin/notifications" />
          <button
            type="button"
            className="rmadl-theme-btn"
            onClick={toggleTheme}
            aria-label={isLight ? 'Switch to dark mode' : 'Switch to light mode'}
            title={isLight ? 'Dark mode' : 'Light mode'}
          >
            <FontAwesomeIcon icon={isLight ? faMoon : faSun} />
          </button>

          <div className="rmadl-user">
            <span className="rmadl-avatar" aria-hidden="true">
              {userData?.Image ? <img src={userData.Image} alt="" /> : <FontAwesomeIcon icon={faUserShield} />}
            </span>
            <span className="rmadl-user-name">{fullName || 'Admin'}</span>
          </div>
        </div>
      </header>

      {/* ---------- Sidebar (drawer on mobile) ---------- */}
      <aside id="rmadl-side" className={`rmadl-side ${menuOpen ? 'is-open' : ''}`} aria-label="Admin menu">
        <nav className="rmadl-nav-wrap">
          {NAV.map((g) => (
            <div key={g.group} className="rmadl-group">
              <span className="rmadl-group-label">{g.group}</span>
              <ul className="rmadl-nav">
                {g.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        `rmadl-nav-item ${
                          isActive ||
                          path.startsWith(item.to.toLowerCase()) ||
                          (item.also || []).some((p) => path.startsWith(p))
                            ? 'is-active'
                            : ''
                        }`
                      }
                    >
                      <FontAwesomeIcon icon={item.icon} />
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="rmadl-side-bottom">
          <button type="button" className="rmadl-leave" onClick={() => setShowLogout(true)}>
            <FontAwesomeIcon icon={faRightFromBracket} />
            Log out
          </button>
        </div>
      </aside>

      {menuOpen && <button type="button" className="rmadl-scrim" aria-label="Close menu" onClick={() => setMenuOpen(false)} />}

      {/* ---------- Page ---------- */}
      <main className="rmadl-main">{childrenWithProps}</main>

      {/* ---------- Logout ---------- */}
      <Modal
        isOpen={showLogout}
        onRequestClose={() => setShowLogout(false)}
        contentLabel="Log out"
        className={`rmadl-modal ${isLight ? 'is-light' : ''}`}
        overlayClassName="rmadl-modal-overlay"
      >
        <h2 className="rmadl-modal-title">Log out?</h2>
        <p className="rmadl-modal-text">You can sign in again any time.</p>
        <div className="rmadl-modal-actions">
          <button type="button" className="rmadl-modal-cancel" onClick={() => setShowLogout(false)}>
            Stay
          </button>
          <button type="button" className="rmadl-modal-confirm" onClick={handleLogout}>
            Log out
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AdminLayout;
